import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../UI/LoadingSpinner';
import { templateService } from '../../services/templateService';
import { webStorageService } from '../../services/webStorageService';

const TemplateManager = ({ user }) => {
  const [templates, setTemplates] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // New template form state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    noteType: '',
    fields: [''],
    isDefault: false
  });

  useEffect(() => {
    loadUserTemplates();
  }, []);

  const loadUserTemplates = async () => {
    try {
      setError(null);

      if (!webStorageService.isAvailable()) {
        setError('Browser storage not available. Please enable localStorage.');
        setIsLoading(false);
        return;
      }

      // Always check cloud FIRST — prevents initializeDefaultTemplates from
      // wiping existing cloud templates on a fresh browser/device
      const { ok, templates: cloudTemplates } = await templateService.syncFromCloud();

      // Cloud reachable and has templates — use them
      if (ok && cloudTemplates && cloudTemplates.length > 0) {
        setTemplates(cloudTemplates);
        const cloudDefault = cloudTemplates.find(t => t.isDefault);
        if (cloudDefault) {
          setDefaultTemplate(cloudDefault.id);
        } else {
          const defaultId = await templateService.getDefaultTemplateId();
          setDefaultTemplate(defaultId);
        }
        return;
      }

      // Cloud UNREACHABLE — never initialize defaults or push here, because doing
      // so on a fresh browser could clobber real (but unread) cloud templates.
      // Show whatever is cached locally and surface a non-destructive warning.
      if (!ok) {
        const cached = await templateService.getUserTemplates();
        setTemplates(cached);
        setDefaultTemplate(await templateService.getDefaultTemplateId());
        setError(
          cached.length > 0
            ? 'Showing locally cached templates — cloud sync is temporarily unavailable. Your saved templates are safe.'
            : 'Could not reach the cloud to load your templates. Please refresh — your saved templates are safe and will appear once the connection is restored.'
        );
        return;
      }

      // Cloud reachable but genuinely EMPTY — safe to fall back to localStorage
      const userTemplates = await templateService.getUserTemplates();
      const defaultId = await templateService.getDefaultTemplateId();

      if (userTemplates.length === 0) {
        // Both cloud and local are empty — safe to initialize defaults
        await templateService.initializeDefaultTemplates();
        const newTemplates = await templateService.getUserTemplates();
        const newDefaultId = await templateService.getDefaultTemplateId();
        setTemplates(newTemplates);
        setDefaultTemplate(newDefaultId);
      } else {
        setTemplates(userTemplates);
        setDefaultTemplate(defaultId);
        // Local has templates but cloud was empty — push them up
        templateService._pushAllToCloud(userTemplates);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      setError('Failed to load templates. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 4000); // Show success for 4 seconds
  };

  const showErrorMessage = (message) => {
    setError(message);
    setTimeout(() => setError(null), 6000); // Show errors for 6 seconds
  };

  const handleCreateTemplate = () => {
    setNewTemplate({
      name: '',
      noteType: '',
      fields: [''],
      isDefault: false
    });
    setEditingTemplate(null);
    setShowCreateForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditTemplate = (template) => {
    setNewTemplate({
      name: template.name,
      noteType: template.noteType || '',
      fields: [...template.fields],
      isDefault: template.id === defaultTemplate
    });
    setEditingTemplate(template);
    setShowCreateForm(true);
  };

  const handleAddField = () => {
    setNewTemplate(prev => ({
      ...prev,
      fields: [...prev.fields, '']
    }));
  };

  const handleRemoveField = (index) => {
    if (newTemplate.fields.length <= 1) return; // Keep at least 1 field
    setNewTemplate(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleFieldChange = (index, value) => {
    setNewTemplate(prev => ({
      ...prev,
      fields: prev.fields.map((field, i) => i === index ? value : field)
    }));
  };

  const handleSaveTemplate = async () => {
    // Validation
    if (!newTemplate.name.trim()) {
      showErrorMessage('Please enter a template name');
      return;
    }

    const validFields = newTemplate.fields.filter(field => field.trim() !== '');
    if (validFields.length < 1) {
      showErrorMessage('Please enter at least 1 section name');
      return;
    }

    try {
      console.log('Saving template:', { newTemplate, validFields }); // Debug log
      
      const templateData = {
        name: newTemplate.name.trim(),
        fields: validFields.map(field => field.trim()),
        noteType: newTemplate.noteType
      };

      let result;
      if (editingTemplate) {
        // Update existing template
        console.log('Updating template:', editingTemplate.id, templateData);
        result = await templateService.updateTemplate(editingTemplate.id, templateData);
      } else {
        // Create new template
        console.log('Creating new template:', templateData);
        result = await templateService.createTemplate(templateData);
      }

      console.log('Template save result:', result); // Debug log

      if (result) {
        // Set as default if requested
        if (newTemplate.isDefault) {
          console.log('Setting as default template:', result.id);
          await templateService.setDefaultTemplate(result.id);
          setDefaultTemplate(result.id);
        }

        // Update local state directly from the saved result.
        // Don't call loadUserTemplates() here — syncFromCloud races the
        // fire-and-forget cloud push and would overwrite localStorage with
        // the stale cloud list, hiding the new template until the next mount.
        if (editingTemplate) {
          setTemplates(prev => prev.map(t => t.id === result.id ? result : t));
        } else {
          setTemplates(prev => [...prev, result]);
        }

        // Close modal
        setShowCreateForm(false);
        setEditingTemplate(null);

        // Show success message
        showSuccessMessage(editingTemplate ? '✓ Template updated!' : '✓ Template created!');

      } else {
        console.error('Template save returned null/false');
        showErrorMessage('Failed to save template. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      showErrorMessage('Failed to save template. Please try again.');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) return;

    try {
      const success = await templateService.deleteTemplate(templateId);
      if (success) {
        // Update state directly — same syncFromCloud race applies to delete.
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        if (templateId === defaultTemplate) {
          const remaining = templates.filter(t => t.id !== templateId);
          setDefaultTemplate(remaining.length > 0 ? remaining[0].id : '');
        }
        showSuccessMessage('Template deleted successfully!');
      } else {
        showErrorMessage('Failed to delete template. Please try again.');
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      showErrorMessage('Failed to delete template. Please try again.');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      const success = await templateService.setDefaultTemplate(templateId);
      if (success) {
        setDefaultTemplate(templateId);
        showSuccessMessage('Default template updated!');
      } else {
        showErrorMessage('Failed to set default template.');
      }
    } catch (error) {
      console.error('Failed to set default template:', error);
      showErrorMessage('Failed to set default template.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="large" text="Loading templates..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white mb-8 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-3">Note Templates</h1>
              <p className="text-blue-100 text-xl">
                Create and manage custom note formats for your practice
              </p>
            </div>
            <button
              onClick={handleCreateTemplate}
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg text-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              + Create New Template
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 text-green-800 px-6 py-4 rounded-xl mb-6 text-lg shadow-lg animate-pulse">
            <div className="flex items-center">
              <div className="text-green-500 mr-3 text-2xl">✓</div>
              <div>{success}</div>
            </div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 text-red-800 px-6 py-4 rounded-xl mb-6 text-lg shadow-lg">
            <div className="flex items-center">
              <div className="text-red-500 mr-3 text-2xl">⚠</div>
              <div>{error}</div>
            </div>
          </div>
        )}

        {/* Templates List */}
        <div className="space-y-6">
          {templates.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
              <div className="text-8xl mb-8 text-gray-300">📋</div>
              <h3 className="text-3xl font-semibold text-gray-800 mb-4">No Templates Created Yet</h3>
              <p className="text-gray-600 text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
                Create your first custom template to match your practice's note format. 
                Templates help standardize your documentation and save time.
              </p>
              <button
                onClick={handleCreateTemplate}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-10 py-4 rounded-xl transition-all duration-200 text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Create Your First Template
              </button>
            </div>
          ) : (
            <div className="grid gap-6">
              {templates.map(template => (
                <div key={template.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <h3 className="text-2xl font-bold text-gray-800">
                          {template.name}
                        </h3>
                        {template.id === defaultTemplate && (
                          <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                            Default Template
                          </span>
                        )}
                        {template.noteType && (
                          <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
                            {template.noteType}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-3 mb-6">
                        {template.fields.map((field, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-lg font-medium border border-blue-200"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                      
                      <div className="text-lg text-gray-500">
                        Created: {new Date(template.createdAt).toLocaleDateString()}
                        {template.updatedAt && template.updatedAt !== template.createdAt && (
                          <span> • Last Updated: {new Date(template.updatedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 ml-6 flex-shrink-0 flex-wrap justify-end">
                      {template.id !== defaultTemplate && (
                        <button
                          onClick={() => handleSetDefault(template.id)}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-6 py-3 rounded-xl transition-colors text-lg whitespace-nowrap flex-shrink-0"
                        >
                          Set as Default
                        </button>
                      )}
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium px-6 py-3 rounded-xl transition-colors text-lg whitespace-nowrap flex-shrink-0"
                      >
                        Edit
                      </button>
                      {templates.length > 1 && (
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 font-medium px-6 py-3 rounded-xl transition-colors text-lg whitespace-nowrap flex-shrink-0"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Template Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-10">
              <h2 className="text-3xl font-bold text-gray-800 mb-8">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h2>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 text-red-800 px-4 py-3 rounded-lg mb-6 text-base flex items-center gap-2">
                  <span className="text-red-500 text-xl">⚠</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Template Name */}
              <div className="mb-8">
                <label className="block text-xl font-semibold text-gray-700 mb-3">
                  Template Name
                </label>
                <input
                  type="text"
                  className="w-full px-6 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="e.g., SOAP Notes, Progress Notes, Initial Assessment"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              {/* Note Type */}
              <div className="mb-8">
                <label className="block text-xl font-semibold text-gray-700 mb-3">
                  Template Type
                </label>
                <p className="text-gray-600 text-lg mb-4">
                  Name the type of documentation this template produces — type anything or choose a common format
                </p>
                <input
                  type="text"
                  list="noteTypeSuggestions"
                  className="w-full px-6 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="e.g., Progress Note, EMDR Session, Group Therapy, Couples Session..."
                  value={newTemplate.noteType}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, noteType: e.target.value }))}
                />
                <datalist id="noteTypeSuggestions">
                  <option value="Progress Note" />
                  <option value="Intake Assessment" />
                  <option value="Termination Summary" />
                  <option value="Group Therapy Note" />
                  <option value="Couples Session Note" />
                  <option value="EMDR Session Note" />
                  <option value="Supervision Note" />
                  <option value="Crisis Assessment" />
                  <option value="Psychiatric Evaluation" />
                  <option value="Case Conceptualization" />
                  <option value="Treatment Plan Update" />
                  <option value="Collateral Contact Note" />
                </datalist>
              </div>

              {/* Field Names */}
              <div className="mb-8">
                <label className="block text-xl font-semibold text-gray-700 mb-3">
                  Note Sections
                </label>
                <p className="text-gray-600 text-lg mb-6">
                  Add the sections you want in your notes (e.g., Presenting Problem, Intervention, Trauma History, Response, Family History)
                </p>
                <div className="space-y-4">
                  {newTemplate.fields.map((field, index) => (
                    <div key={index} className="flex gap-4 items-center">
                      <div className="flex-1">
                        <input
                          type="text"
                          className="w-full px-6 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="Add section name here..."
                          value={field}
                          onChange={(e) => handleFieldChange(index, e.target.value)}
                        />
                      </div>
                      {newTemplate.fields.length > 2 && (
                        <button
                          onClick={() => handleRemoveField(index)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 font-medium px-4 py-4 rounded-xl transition-colors text-lg"
                          title="Remove this section"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddField}
                  className="mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-6 py-3 rounded-xl transition-colors text-lg"
                >
                  + Add Another Section
                </button>
              </div>

              {/* Set as Default */}
              <div className="mb-10">
                <label className="flex items-center gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
                    checked={newTemplate.isDefault}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, isDefault: e.target.checked }))}
                  />
                  <span className="text-lg text-gray-700">Set as my default template</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setError(null);
                    setSuccess(null);
                  }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-8 py-4 rounded-xl transition-colors text-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl transition-colors text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  {editingTemplate ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;